import { useState, useEffect, useCallback } from 'react'

const GuidedTour = ({ steps, tourKey, onComplete }) => {
  const [current, setCurrent] = useState(0)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const storageKey = `tour_completed_${tourKey}`

  useEffect(() => {
    if (window.localStorage.getItem(storageKey)) return
    const timer = setTimeout(() => setVisible(true), 500)
    return () => clearTimeout(timer)
  }, [storageKey])

  const updatePosition = useCallback(() => {
    if (!visible || !steps[current]) return
    const el = document.querySelector(steps[current].target)
    if (el) {
      const rect = el.getBoundingClientRect()
      const tooltipWidth = 384 // max-w-sm = 24rem = 384px
      let left = rect.left + window.scrollX
      // Clamp so tooltip doesn't overflow right edge
      if (left + tooltipWidth > window.innerWidth - 16) {
        left = window.innerWidth - tooltipWidth - 16
      }
      left = Math.max(16, left)
      setPosition({
        top: rect.bottom + window.scrollY + 12,
        left
      })
    }
  }, [visible, current, steps])

  useEffect(() => {
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [updatePosition])

  // Fire onEnter callback when step changes
  useEffect(() => {
    if (!visible || !steps[current]) return
    const step = steps[current]
    if (step.onEnter) step.onEnter()
  }, [visible, current, steps])

  const finish = () => {
    setVisible(false)
    window.localStorage.setItem(storageKey, 'true')
    if (onComplete) onComplete()
  }

  const next = () => {
    if (current < steps.length - 1) {
      setCurrent(current + 1)
    } else {
      finish()
    }
  }

  const prev = () => {
    if (current > 0) setCurrent(current - 1)
  }

  if (!visible || !steps[current]) return null

  return (
    <>
      <div
        className='fixed inset-0 bg-black bg-opacity-30 z-40'
        onClick={finish}
      />
      <div
        className='absolute z-50 bg-white rounded-lg shadow-xl p-5 max-w-sm w-full'
        style={{ top: position.top, left: position.left }}
      >
        <div className='text-xs text-gray-400 mb-1'>
          Step {current + 1} of {steps.length}
        </div>
        <h3 className='text-base font-bold mb-1'>{steps[current].title}</h3>
        <p className='text-sm text-gray-600 mb-4'>{steps[current].content}</p>
        <div className='flex justify-between items-center'>
          <button
            className='text-sm text-gray-400 hover:underline'
            onClick={finish}
          >
            Skip tour
          </button>
          <div className='flex gap-2'>
            {current > 0 && (
              <button
                className='text-sm text-blue-6 hover:underline'
                onClick={prev}
              >
                Back
              </button>
            )}
            <button
              className='text-sm bg-blue-6 hover:bg-blue-7 text-white rounded px-3 py-1'
              onClick={next}
            >
              {current < steps.length - 1 ? 'Next' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default GuidedTour
